<!-- SelectObservationTypeFilter.vue -->
<!--
  Mini-modal de filtro por TIPO de observação.
  Substitui o antigo filtro "Com observação".

  Layout (conforme mockup):
    Pesquisar
    [__________________]

    Tipo de sinalização
    ☐ Acertos   ☐ Erros

    Leitura
    ☐ Lidas     ☐ Não lidas

  - Campo "Pesquisar": busca LOCAL e independente da busca global. Filtra as
    notas cujo campo "observação" (observation) contém o termo, respeitando o
    tipo marcado (AND). Debounce de 400ms.
  - Checkboxes: mutuamente exclusivos (marcar um desmarca o outro) — no máximo
    um tipo selecionado por vez.

  Comportamento dos tipos:
    - Nenhum marcado → sem filtro por tipo (todas as notas, com ou sem observação).
    - Um marcado     → apenas as notas cujo observationType for o selecionado.
    - Auto-aplica: cada marcar/desmarcar dispara @change (rebusca imediata).

  Comportamento da leitura (quem marca como lida é o CRIADOR da nota, em
  MarkObservationAsRead.vue) — grupo INDEPENDENTE do tipo, combina em AND:
    - Nenhum marcado → sem filtro por leitura.
    - "Lidas"        → notas já marcadas como lidas pelo criador.
    - "Não lidas"    → notas com sinalização (observationType não-nulo: certo,
                       errado ou observação com texto) ainda não lidas. Não exige
                       observação preenchida — basta a marcação de certo/errado.
    - Exclusivos entre si, mas livres em relação a Acertos/Erros.

  Props:
    isOpen            — controla visibilidade
    modelValue        — array de tipos selecionados (v-model), ex: ["Correct"] (no máximo 1 item)
    observationSearch — termo da busca local na observação (v-model:observation-search)
    readState         — "" | "read" | "unread" (v-model:read-state)

  Emits:
    update:modelValue        — novo array de tipos
    change                   — usuário marcou/desmarcou um tipo (rebuscar)
    update:observationSearch — novo termo de busca na observação (debounced)
    search                   — busca na observação alterada (rebuscar, debounced)
    update:readState         — novo estado de leitura
    close                    — pedido de fechamento
-->
<script setup>
import { ref, watch } from "vue";
import AnchoredPanel from "@/components/Generics/AnchoredPanel.vue";
import debounce from "lodash.debounce";

const props = defineProps({
  isOpen: { type: Boolean, default: false },
  modelValue: { type: Array, default: () => [] },
  observationSearch: { type: String, default: "" },
  readState: { type: String, default: "" }, // "" | "read" | "unread"
});

const emit = defineEmits([
  "update:modelValue",
  "change",
  "update:observationSearch",
  "search",
  "update:readState",
  "close",
]);

// Valores alinhados ao campo observationType das notas (PascalCase),
// mesmos usados em NoteList.vue e EditNoteObservationForm.vue.
const options = [
  { value: "Correct", label: "Acertos", color: "#22c55e" },
  { value: "Incorrect", label: "Erros", color: "#ef4444" },
];

function isChecked(value) {
  return props.modelValue.includes(value);
}

// Mutuamente exclusivos: marcar um tipo substitui o outro (no máximo 1 selecionado).
function toggle(value) {
  const next = props.modelValue.includes(value) ? [] : [value];
  emit("update:modelValue", next);
  emit("change");
}

// ── Leitura ────────────────────────────────────────────────────────────────
// Grupo próprio: exclusivo entre si ("lidas" e "não lidas" se anulariam),
// porém independente dos tipos acima — pode ser marcado com ou sem Acertos/Erros.
const readOptions = [
  { value: "read", label: "Lidas", color: "#3b82f6" },
  { value: "unread", label: "Não lidas", color: "#9ca3af" },
];

function isReadChecked(value) {
  return props.readState === value;
}

function toggleReadState(value) {
  emit("update:readState", props.readState === value ? "" : value);
  emit("change");
}

// ── Pesquisar (busca local na observação, com debounce) ────────────────────
const inputRef = ref(null);
const localSearch = ref(props.observationSearch);

// Sincroniza quando o termo muda por fora (ex.: reset de filtros).
// Evita sobrescrever o que o usuário está digitando aqui.
watch(
  () => props.observationSearch,
  (val) => {
    if (val !== localSearch.value) localSearch.value = val;
  },
);

const debouncedSearch = debounce(() => {
  emit("update:observationSearch", localSearch.value);
  emit("search");
}, 400);

watch(localSearch, () => {
  debouncedSearch();
});

// Ao abrir, sincroniza o campo de pesquisa; o foco vai no @opened do painel,
// quando o campo já existe e está posicionado.
watch(
  () => props.isOpen,
  (open) => {
    if (open) localSearch.value = props.observationSearch;
  },
);

function onKeydown(e) {
  if (e.key === "Enter") {
    debouncedSearch.flush();
  }
  if (e.key === "Escape") emit("close");
}
</script>

<template>
  <AnchoredPanel
    :open="isOpen"
    placement="side"
    align="left"
    :gap="8"
    @close="emit('close')"
    @opened="inputRef?.focus()"
  >
    <div
      class="obs-type-container"
      role="dialog"
      aria-label="Filtrar por tipo de observação"
      @click.stop
      @mousedown.stop
      @keydown="onKeydown"
    >
      <!-- ── Busca na observação ── -->
      <div class="search-section">
        <input
          ref="inputRef"
          v-model="localSearch"
          type="text"
          class="field-input"
          placeholder="Buscar na observação"
          autocomplete="off"
        />
      </div>

      <!-- ── Tipo de sinalização ── -->
      <div class="section-label">Tipo de sinalização</div>
      <div class="types-row">
        <label
          v-for="option in options"
          :key="option.value"
          class="type-item"
          :style="{ '--type-color': option.color }"
        >
          <input
            type="checkbox"
            class="type-checkbox"
            :checked="isChecked(option.value)"
            @change="toggle(option.value)"
          />
          <span class="type-label">{{ option.label }}</span>
        </label>
      </div>

      <!-- ── Leitura (independente do tipo) ── -->
      <div class="section-label">Leitura</div>
      <div class="types-row">
        <label
          v-for="option in readOptions"
          :key="option.value"
          class="type-item"
          :style="{ '--type-color': option.color }"
        >
          <input
            type="checkbox"
            class="type-checkbox"
            :checked="isReadChecked(option.value)"
            @change="toggleReadState(option.value)"
          />
          <span class="type-label">{{ option.label }}</span>
        </label>
      </div>
    </div>
  </AnchoredPanel>
</template>

<style scoped>
/* === Mesma linguagem visual de SelectFieldFilter.vue === */

.obs-type-container {
  background-color: var(--surface);
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
  border: 1px solid var(--border-color);
  width: max-content;
  min-width: 210px;
  max-width: 260px;
  display: flex;
  flex-direction: column;
}

/* Título de seção no mesmo desenho do NoteFilter. Não usar `.modal-header`:
   é a classe canônica do cabeçalho de modal, com fundo e padding próprios. */
.section-label {
  margin-top: 4px;
  padding: 2px 12px;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--text-subtle);
  user-select: none;
}

.search-section {
  padding: 10px 12px 4px;
}

.field-input {
  background-color: #f7f7f7;
  border: 1px solid var(--border-color);
  color: var(--text-dark);
  font-size: 0.82rem;
  border-radius: 8px;
  display: block;
  width: 100%;
  padding: 5px 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  outline: none;
  box-sizing: border-box;
}

.field-input:focus {
  border-color: var(--brand-light);
  box-shadow: var(--focus-ring-field);
}

.types-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 12px;
  padding: 4px 12px 10px;
}

.type-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 0.82rem;
  font-weight: 500;
  color: var(--text-dark);
  white-space: nowrap;
  user-select: none;
}

.type-checkbox {
  width: 0.9rem;
  height: 0.9rem;
  border: 1px solid var(--text-subtle);
  border-radius: 3px;
  background-color: var(--surface);
  cursor: pointer;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  position: relative;
  margin: 0;
  flex-shrink: 0;
  transition: all 0.15s ease-in-out;
}

.type-checkbox:hover {
  border-color: var(--type-color, var(--brand-light));
}

.type-checkbox:checked {
  background-color: var(--type-color, var(--brand-light));
  border-color: var(--type-color, var(--brand-light));
}

.type-checkbox:checked::after {
  content: "";
  position: absolute;
  left: 4px;
  top: 1px;
  width: 4px;
  height: 8px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.type-checkbox:focus,
.type-checkbox:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--type-color, var(--brand-light)) 35%, transparent);
}
</style>
